
// Added By Farhan Khan Khan on 24-Dec-2025
// Updated on 24-Mar-2026 by Farhan Khan: Unified financial calculations and project modeling
import React, { useState, useEffect, useMemo } from "react";
import { Button, Col, Form, Row, Table, Card, ButtonGroup } from "react-bootstrap";
import Select from "react-select";
import PubSub from "pubsub-js";
import Confirm from "../Confirm";

// GST Rate as per requirement (8.9%)
const GST_RATE = 8.9;
const DEFAULT_SUBSIDY = -78000;

const QuotationLineItems = ({ items, setItems, productOptions, quotation, setQuotation, readOnly = false }) => {
  const [showSubsidyConfirm, setShowSubsidyConfirm] = useState(false);
  const [subsidyEditEnabled, setSubsidyEditEnabled] = useState(false);

  // Default Subsidy if missing (renamed to subsidy_amount)
  const currentSubsidy = quotation?.subsidy_amount !== undefined && quotation?.subsidy_amount !== null ? quotation.subsidy_amount : DEFAULT_SUBSIDY;
  const currentGstType = quotation?.gst_type || "Inclusive";
  const currentDiscount = quotation?.total_discount || 0;

  // 🎯 STEP 1: REMOVE STATE FOR DERIVED VALUES
  // No local state hooks representing the final computed totals
  const formatAmount = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return "0.00";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const addItemRow = () => {
    const newItems = [
      ...items,
      {
        id: `temp_${Date.now()}`,
        product_id: null,
        product_name: "Rooftop Solar Power Plant",
        product_description: "",
        quantity: 1,
        phase_qty: "",
        unit_price: "",
        discount: 0,
        gross_amount: 0,
        net_amount: 0,
      },
    ];
    setItems(newItems);
  };

  const removeItemRow = (indexToRemove) => {
    const newItems = items.filter((_, index) => index !== indexToRemove);
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    const item = newItems[index];

    if (field === "product_id" && value) {
      item.product_id = value.value;
      item.product_name = value.label;
      const selectedProduct = productOptions.find(p => p.value === value.value);
      if (selectedProduct && selectedProduct.product) {
        const p = selectedProduct.product;
        item.product_description = p.description || "";
        const unitPrice = p.product_unit_price ?? p.selling_price ?? p.unit_price ?? "";
        item.unit_price = unitPrice !== "" && unitPrice != null ? String(unitPrice) : "";
      }
    } else {
      if (field === "discount") {
        const discountVal = parseFloat(value) || 0;
        if (discountVal < 0) {
          PubSub.publish("RECORD_ERROR_TOAST", {
            title: "Validation Error",
            message: "Discount cannot be negative",
          });
          return;
        }
      }
      item[field] = value;
    }

    // Recalculate line total
    const cap = parseFloat(item.plant_capacity_required_by_client) || 0;
    const price = parseFloat(item.unit_price) || 0;
    const discount = parseFloat(item.discount) || 0;
    item.gross_amount = cap * price;
    item.net_amount = item.gross_amount - discount;

    setItems(newItems);
  };

  // 🎯 STEP 2: COMPUTE VALUES DIRECTLY ON RENDER (SINGLE SOURCE OF TRUTH)
  const pc = Number(items.reduce((sum, item) => sum + (Number(item.net_amount) || 0), 0)) || 0;

  // 🎯 STEP 6: HANDLE GST MODE
  const gstAmt = Number(
    currentGstType === "Inclusive"
      ? (pc / (100 + GST_RATE)) * GST_RATE
      : pc * (GST_RATE / 100)
  ) || 0;

  // Case A (Inclusive): Total Payable = Project Cost (pc) - Subsidy - Discount
  // Case B (Exclusive): Total Payable = Project Cost (pc) + GST - Subsidy - Discount
  const sub = Math.abs(Number(currentSubsidy) || 0);
  const disc = Number(currentDiscount) || 0;

  const totalWithTax = currentGstType === "Inclusive" ? pc : pc + (Number(gstAmt) || 0);

  // Requirement: Total Payable DOES NOT include subsidy
  // Formula: Project Cost (Inclusive of GST or + GST) - Discount
  const totalPayable = Math.max(0, totalWithTax - disc);

  // Requirement: Effective Price DOES include subsidy (UNCHANGED)
  // Formula: Project Cost (Inclusive of GST or + GST) - Subsidy - Discount
  const effectivePrice = Math.max(0, totalWithTax - sub - disc);

  // 🎯 STEP 7: DEBUG LOG
  console.log("FINAL VALUES", {
    pc,
    gstAmt,
    sub,
    disc,
    effectivePrice,
    totalPayable
  });

  // 🎯 SYNC TO PARENT WITHOUT LOCAL STATE DEPENDENCY
  useEffect(() => {
    if (setQuotation) {
      setQuotation(prev => {
        // Prevent infinite render looping
        if (
          prev?.project_cost === pc &&
          prev?.gst_amount === gstAmt &&
          prev?.effective_price === effectivePrice &&
          prev?.payable_amount === totalPayable &&
          prev?.subtotal === pc &&
          prev?.grand_total === totalWithTax
        ) {
          return prev;
        }

        return {
          ...prev,
          subtotal: pc,
          tax_gst: gstAmt,
          grand_total: totalWithTax,
          payable_amount: totalPayable,
          effective_price: effectivePrice,
          project_cost: pc,
          gst_amount: gstAmt,
          total_amount: totalWithTax
        };
      });
    }
  }, [pc, gstAmt, totalWithTax, effectivePrice, totalPayable, setQuotation]);

  const displayEffectivePrice = readOnly ? (quotation?.effective_price ?? quotation?.payable_amount ?? effectivePrice) : effectivePrice;
  const displayTotalPayable = readOnly ? (quotation?.payable_amount ?? quotation?.grand_total ?? totalPayable) : totalPayable;
  const displayPc = readOnly ? (quotation?.project_cost ?? quotation?.subtotal ?? pc) : pc;
  const displayGstAmt = readOnly ? (quotation?.gst_amount ?? quotation?.tax_gst ?? gstAmt) : gstAmt;

  // Handle Updates
  const toggleGstType = (type) => {
    setQuotation(prev => ({ ...prev, gst_type: type }));
  };

  const handleSubsidyUpdate = (val) => {
    setQuotation(prev => ({ ...prev, subsidy_amount: val }));
  };

  const handleGlobalDiscountUpdate = (val) => {
    const discountVal = Number(val) || 0;
    if (discountVal < 0) {
      setQuotation(prev => ({ ...prev, total_discount: 0, discount: 0 }));
    } else {
      setQuotation(prev => ({ ...prev, total_discount: discountVal, discount: discountVal }));
    }
  };

  const confirmSubsidyEdit = () => {
    setSubsidyEditEnabled(true);
    setShowSubsidyConfirm(false);
  };

  return (
    <div className="mt-4">
      {showSubsidyConfirm && (
        <Confirm
          show={showSubsidyConfirm}
          onHide={() => setShowSubsidyConfirm(false)}
          onConfirm={confirmSubsidyEdit}
          title="Warning"
          message="Changing subsidy will impact the final calculation. Are you sure?"
          table="financial_subsidy"
        />
      )}

      {/* Header Section with Add Button and GST Toggle */}
      <Row className="mb-3 align-items-center">
        <Col lg={6} md={6} sm={12}>
          <h5 className="mb-1" style={{ color: "#364794", fontWeight: "600" }}>
            Quotation Financial Items
          </h5>
          <p className="text-muted small mb-0">Project modeling and pricing details</p>
        </Col>
        <Col lg={6} md={6} sm={12} className="text-end d-flex justify-content-end align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted fw-bold">GST MODEL:</span>
            <ButtonGroup size="sm">
              <Button
                variant={currentGstType === "Inclusive" ? "primary" : "outline-primary"}
                onClick={() => !readOnly && toggleGstType("Inclusive")}
                disabled={readOnly}
              >
                Inclusive
              </Button>
              <Button
                variant={currentGstType === "Exclusive" ? "primary" : "outline-primary"}
                onClick={() => !readOnly && toggleGstType("Exclusive")}
                disabled={readOnly}
              >
                Exclusive
              </Button>
            </ButtonGroup>
          </div>
          {!readOnly && (
            <Button
              size="sm"
              className="btn-create"
              onClick={addItemRow}
              style={{ fontWeight: "600", borderRadius: "8px" }}
            >
              <i className="fa fa-plus me-2"></i> Add Row
            </Button>
          )}
        </Col>
        <span style={{ color: "#ff0000ff", fontWeight: "600", textAlign: "right" }}>Note: Calculation is based on Plant Capacity (kW) multiplied by the applicable Rate per kW.</span>
      </Row>

      {/* Table Section */}
      <div className="table-responsive rounded-3 shadow-sm mb-4">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead className="bg-primary text-white">
            <tr style={{ backgroundColor: "#364794" }}>
              <th style={{ width: "25%", color: "#000000 ", padding: "12px", textAlign: "center" }}>Product Name</th>
              <th style={{ width: "20%", color: "#000000", padding: "12px", textAlign: "center" }}>Product Specs</th>
              <th style={{ width: "10%", color: "#000000", padding: "12px", textAlign: "center" }}> Phase</th>
              <th style={{ width: "15%", color: "#000000", padding: "12px", textAlign: "center" }}>Plant Capacity (KW)</th>
              <th style={{ width: "15%", color: "#000000", padding: "12px", textAlign: "center" }}>Rate / KW</th>
              {/* <th style={{ width: "10%", color: "#000000", padding: "12px", textAlign: "center" }}>Discount</th> */}
              <th style={{ width: "10%", color: "#000000", padding: "12px", textAlign: "center" }}>Total Amount</th>
              {!readOnly && <th style={{ width: "5%", color: "#000000", padding: "12px", textAlign: "center" }}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.id || index}>
                <td className="p-2">
                  {readOnly ? (
                    <div className="fw-bold" style={{ padding: "8px 12px", fontSize: "0.9rem", textAlign: "center" }}>
                      {item.product_name || item.product?.name || "-"}
                    </div>
                  ) : (
                    <Select
                      placeholder="Select Product..."
                      onChange={(selected) => handleItemChange(index, "product_id", selected)}
                      options={productOptions}
                      value={productOptions.find(p => p.value === item.product_id)}
                      isClearable={!readOnly}
                      isDisabled={readOnly}
                      styles={{
                        control: (base) => ({ ...base, minHeight: "38px", borderRadius: "6px", textAlign: "center" }),
                        option: (base) => ({
                          ...base,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          padding: "8px 12px"
                        }),
                        singleValue: (base) => ({
                          ...base,
                          whiteSpace: "normal"
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                          minWidth: "250px"
                        })
                      }}
                    />
                  )}
                </td>
                <td className="p-2">
                  <Form.Control
                    size="sm"
                    as="textarea"
                    rows={1}
                    value={item.product_description || ""}
                    onChange={(e) => handleItemChange(index, "product_description", e.target.value)}
                    style={{ fontSize: "0.85rem", borderRadius: "6px", textAlign: "center" }}
                    disabled={readOnly}
                  />
                </td>
                <td className="p-2">
                  <Form.Control
                    size="sm"
                    type="text"
                    value={item.phase_qty || ""}
                    onChange={(e) => handleItemChange(index, "phase_qty", e.target.value)}
                    // className="text-center"
                    style={{ fontWeight: "400", textAlign: "center" }}
                    disabled
                  />
                </td>
                <td className="p-2">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={item.plant_capacity_required_by_client || ""}
                    onChange={(e) => handleItemChange(index, "plant_capacity_required_by_client", e.target.value)}
                    className="text-center"
                    style={{ fontWeight: "600", textAlign: "center" }}
                    disabled
                  />
                </td>
                <td className="p-2">
                  <Form.Control
                    size="sm"
                    type="number"
                    value={item.unit_price || ""}
                    style={{ fontWeight: "600", textAlign: "center" }}
                    onChange={(e) => handleItemChange(index, "unit_price", e.target.value)}
                  // className="text-end"
                  // disabled={readOnly}
                  />
                </td>

                {/* <td className="p-2">
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    value={item.discount || 0}
                    onChange={(e) => handleItemChange(index, "discount", e.target.value)}
                    style={{ fontWeight: "400", textAlign: "center" }}
                    // className="text-end"
                    disabled={readOnly}
                  />
                </td> */}
                <td className="p-2 fw-bold" style={{ verticalAlign: "middle", textAlign: "center" }}>
                  ₹ {formatAmount(item.net_amount || item.gross_amount)}
                </td>
                {!readOnly && (
                  <td className="p-2 text-center" style={{ verticalAlign: "middle" }}>
                    <Button variant="link" className="text-danger p-0" onClick={() => removeItemRow(index)}>
                      <i className="fa fa-trash"></i>
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Structured Summary Table - Added on 24-Mar-2026 by Farhan Khan */}
      <h3 className="financial-heading">Quotation Financial Items</h3>

      <div className="d-flex justify-content-end mb-4">
        <div style={{ minWidth: "400px" }}>
          <table className="financial-summary-table">
            <tbody>
              <tr>
                <td>Project Cost</td>
                <td className="fw-bold">₹ {formatAmount(displayPc)}</td>
              </tr>
              <tr>
                <td>GST ({GST_RATE}% {currentGstType})</td>
                <td className="fw-bold text-danger">₹ {formatAmount(displayGstAmt)}</td>
              </tr>
              <tr onClick={() => !readOnly && !subsidyEditEnabled && setShowSubsidyConfirm(true)} style={{ cursor: (readOnly || subsidyEditEnabled) ? "auto" : "pointer" }}>
                <td>
                  Subsidy {(!readOnly && !subsidyEditEnabled) && <i className="fa fa-lock small ms-1 text-muted"></i>}
                </td>
                <td className="text-end">
                  {readOnly ? (
                    <span className="fw-bold text-success">
                      ₹ {formatAmount(currentSubsidy)}
                    </span>
                  ) : (
                    <Form.Control
                      size="sm"
                      type="number"
                      className={`text-end border-0 bg-transparent fw-bold text-success p-0 ${(!subsidyEditEnabled || readOnly) ? "pe-none" : ""}`}
                      disabled={!subsidyEditEnabled || readOnly}
                      value={currentSubsidy ?? ""}
                      onChange={(e) => {
                        console.log("SUBSIDY VALUE:", e.target.value);
                        handleSubsidyUpdate(e.target.value);
                      }}
                      style={{ width: "100%", boxShadow: "none" }}
                    />
                  )}
                </td>
              </tr>
              <tr>
                <td>Discount (Extra)</td>
                <td className="text-end">
                  {readOnly ? (
                    <span className="fw-bold text-danger">
                      ₹ {formatAmount(currentDiscount)}
                    </span>
                  ) : (
                    <Form.Control
                      size="sm"
                      type="number"
                      min="0"
                      className="text-end border-0 bg-transparent fw-bold text-danger p-0"
                      value={currentDiscount}
                      onChange={(e) => handleGlobalDiscountUpdate(e.target.value)}
                      style={{ width: "100%", boxShadow: "none" }}
                      disabled={readOnly}
                    />
                  )}
                </td>
              </tr>
              <tr>
                <td>Effective Price (Net Price)</td>
                <td className="fw-bold text-success text-end">
                  ₹ {formatAmount(displayEffectivePrice)}
                </td>
              </tr>
              <tr className="total-row">
                <td className="h6 mb-0 py-3">Total Amount Payable</td>
                <td className="h5 mb-0 py-3 text-primary fw-bold text-end">
                  ₹ {formatAmount(displayTotalPayable)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuotationLineItems;
